/**
 * Launch pad for a cross-host session transfer — the mirror of
 * ``AuthContinue``, on the other side of the hop.
 *
 * A session cookie is host-only outside the platform zone, so a link
 * from here to a tenant host arrives with no session and renders a
 * sign-in form. The fix is a one-time token — but the token can only
 * be minted by the host that HOLDS the session, which is this one.
 * A link straight to the destination therefore cannot carry it, and
 * that is what this route exists to fix: mint here, forward there,
 * redeem on arrival.
 *
 * Why a route and not an ``onClick`` on each link: the call sites are
 * menu entries ("open my workspace", "open my buyer team"). Turning
 * them into buttons so they can run a mutation before navigating costs
 * middle-click, ⌘-click and "open in new tab" — the ordinary way to
 * open a second workspace beside the one you are in. A same-origin
 * ``href`` keeps all of that and moves the ceremony here.
 *
 * 🚨 Neither parameter is trusted. ``next`` arrives in a query string,
 * so it is attacker-supplied by definition; it is re-validated as a
 * path here AND again by ``AuthContinue`` on the far side. ``slug`` is
 * checked by the API, which 404s a slug the caller has no membership
 * in — the same answer it gives for a slug that does not exist, so
 * this page cannot be used to enumerate tenants either.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useStartSessionTransfer } from "@/api/queries/auth";
import { safeNextPath } from "@/pages/AuthContinue";
import { tenantHopUrl } from "@/shell/resolveShell";

interface Props {
  /** Seam for tests — jsdom does not navigate. */
  onDone?: (url: string) => void;
}

export default function AuthHop({ onDone }: Props): JSX.Element {
  const { t } = useTranslation("saas");
  const [failed, setFailed] = useState(false);
  const start = useStartSessionTransfer();
  // Mint exactly once. StrictMode fires effects twice in dev, and each
  // extra mint is a live bearer credential nobody will ever redeem.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const slug = params.get("slug");
    const next = safeNextPath(params.get("next"));
    if (!slug || !next) {
      setFailed(true);
      return;
    }

    start.mutate(
      { slug },
      {
        onSuccess: (data) => {
          // ``transfer_token`` null = the API says this hop needs no
          // handover; ``tenantHopUrl`` then builds the plain path.
          const url = tenantHopUrl(next, data.slug, data.transfer_token);
          if (onDone) onDone(url);
          // Host comes from the API's ``data.slug``, path from ``safeNextPath``.
          // nosemgrep: javascript.browser.security.open-redirect.js-open-redirect
          else window.location.replace(url);
        },
        onError: () => setFailed(true),
      },
    );
    // ``start`` is a fresh object each render; the ref is what makes
    // this run once, so depending on it would only re-arm the guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDone]);

  if (failed) {
    return (
      <main
        className="mx-auto max-w-md px-6 py-16 text-center"
        data-testid="auth-hop-failed"
      >
        <h1 className="text-xl font-semibold text-ink">
          {t("authContinue.failedTitle")}
        </h1>
        <p className="mt-2 text-sm text-ink-2">{t("authContinue.failedBody")}</p>
      </main>
    );
  }

  return (
    <main
      className="mx-auto max-w-md px-6 py-16 text-center"
      data-testid="auth-hop"
      aria-live="polite"
    >
      <span className="mx-auto flex h-10 w-10 items-center justify-center">
        <span className="h-2.5 w-2.5 animate-ping rounded-full bg-accent" />
      </span>
      <p className="mt-4 text-sm text-ink-2">{t("authContinue.working")}</p>
    </main>
  );
}
